import { faLineColumns } from "@fortawesome/pro-regular-svg-icons";
import { faAngleLeft, faAngleRight, faTimes } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Badge, Modal, Popconfirm, Tabs, Tooltip } from "antd";
import Title from "antd/lib/typography/Title";
import { filter, findIndex } from "lodash";
import React, { useEffect, useRef } from "react";
import { INavTabs } from "../types/interfaces";
const { TabPane } = Tabs;

function getSRTabKeyIfExisting(panes: Array<INavTabs.TabPaneProps>, newTabAddionalId: string | undefined): string | undefined {
    let tabKey;
    for (let tab of (panes || [])) {
        if (newTabAddionalId && (newTabAddionalId === tab?.additionalId)) {
            tabKey = tab.key;
            break;
        }
    }
    return tabKey;
}

const NavTabs: React.FC<INavTabs.NavProps> = ({ defaultTabKey, activeKey, panes, tabToAdd, tabToUpdate, tabToRemove,
    onChange, onTabAddition, onTabUpdate, onTabRemoval, extraContent, isMobileView, keepUniqueAdditionalId, mainTabTitle }) => {
    const extraContentRef = useRef<HTMLDivElement>(null);
    const MAX_TAB_COUNT = 10;
    const isNotfModule=mainTabTitle === "Notices";

    useEffect(() => {
        if (tabToAdd) {
            addPane(tabToAdd);
        }
    }, [tabToAdd])

    useEffect(() => {
        if (tabToUpdate) updatePane(tabToUpdate);
    }, [tabToUpdate])

    useEffect(() => {
        if (tabToRemove) removePane(tabToRemove);
    }, [tabToRemove])

    const addPane = (tabObjectList: Array<INavTabs.TabPaneProps>) => {
        let currPanes = panes || [];
        let actvKey = tabObjectList?.[0]?.key;
        let tabAdded: boolean = false;
        for (let tabIndx = 0; tabIndx < tabObjectList?.length; tabIndx++) {
            const tabObject = tabObjectList[tabIndx];
            const existingTabKeyWithSameSR = getSRTabKeyIfExisting(currPanes, tabObject?.additionalId);
            if (keepUniqueAdditionalId && existingTabKeyWithSameSR) {
                if (tabObjectList?.length === 1 || (tabIndx === tabObjectList?.length - 1 && !tabAdded)) {
                    onChange(existingTabKeyWithSameSR);
                    break;
                } else
                    continue;
            } else {
                if (currPanes?.length >= MAX_TAB_COUNT) {
                    if (!tabAdded) onChange(defaultTabKey || "-1");
                    showMessage(tabObject.name === "create-sr" ? true : false, mainTabTitle);
                    break;
                } else {
                    currPanes.unshift({ ...tabObject });
                    actvKey = tabObject?.key;
                    tabAdded = true;
                }
            }
        }
        if (tabAdded)
            onTabAddition(currPanes, actvKey);
    };

    const updatePane = (tabObjectList: Array<INavTabs.TabPaneProps>) => {
        const tabPanes = panes || [];
        for (const tabObject of tabObjectList) {
            const tabKey = tabObject.key;
            const indexToUpdate = findIndex(tabPanes, (pane: INavTabs.TabPaneProps) => pane.key === tabKey);
            if (indexToUpdate >= 0) {
                tabPanes[indexToUpdate] = tabObject;
            }
        }
        onTabUpdate(tabPanes);
    }

    const removePane = (tabsToRemove: Array<INavTabs.TabPaneProps>) => {
        let lastIndex = -1, newActiveKey = activeKey;
        const tabPanes = panes || [];

        const isSingleTab = tabsToRemove?.length === 1;
        const targetKey = isSingleTab ? tabsToRemove?.[0]?.key : undefined;
        const tabsKeyList = tabsToRemove?.map(tab => tab?.key);

        const panesAfterRemoval = filter(tabPanes, (pane: INavTabs.TabPaneProps, indx: number) => {
            if (isSingleTab) {
                if (pane.key === targetKey) lastIndex = indx - 1;
                return pane.key !== targetKey;
            } else {
                return !tabsKeyList?.includes(pane.key);
            }
        })

        if (panesAfterRemoval.length > 0 && activeKey === targetKey && isSingleTab) {
            if (lastIndex >= 0) {
                newActiveKey = panesAfterRemoval[lastIndex].key;
            } else {
                newActiveKey = panesAfterRemoval[0].key;
            }
        } else if (!isSingleTab) {
            newActiveKey = defaultTabKey || "-1";
        }
        onTabRemoval(panesAfterRemoval, newActiveKey);
    }

    const showMessage = (isCreating: boolean, mainTabTitle?: string) => {
        Modal.info({
            className: `tab-count-info ${isMobileView && "mbl-view"}`,
            title: (<div>
                <Title style={{ fontSize: "28px", padding: 0, margin: 0 }} className="color-blue-5">
                    {`Please close existing tabs before ${isCreating ? "creating a new" : "viewing a"} ${isNotfModule ? "Notification." : "Service Request."}`}
                </Title>
                <br />
                <div className="color-gray-5 padding-bottom-md">{`Max tab count of ${MAX_TAB_COUNT} reached.`}</div>
            </div>),
            icon: null,
            okText: "OK"
        });
    }

    const noTabsPresent = !panes || panes.length === 0;
    const isDefaultSelected = defaultTabKey === activeKey;
    const defaultExtraContentWidth = extraContent ? 267 : 0;
    const extraContentWidth = extraContentRef?.current?.clientWidth || defaultExtraContentWidth;
    return <>
        <Tabs
            hideAdd
            activeKey={activeKey}
            onChange={onChange}
            type="editable-card"
            tabBarExtraContent={isMobileView ? extraContent : undefined}
            tabBarStyle={{ borderBottom: 0 }}
            style={{ height: (noTabsPresent && !defaultTabKey) ? 0 : "auto" }}
            renderTabBar={(props, DefaultTabBar) =>
                <div id="tab-container" style={{ position: "relative", width: "100%" }}>
                    <div style={isMobileView ? {} : { maxWidth: !extraContent ? "100%" : `calc(100% - ${(extraContentWidth + 20)}px` }}>
                        <DefaultTabBar
                            {...props}
                            style={{ ...props.style, height: "40px", marginBottom: !isDefaultSelected ? "16px" : 0 }}
                            prevIcon={<div style={{ display: "flex", height: "100%" }}>
                                <FontAwesomeIcon style={{ margin: 'auto' }} icon={faAngleLeft} />
                            </div>}
                            nextIcon={<div style={{ display: "flex", height: "100%" }}>
                                <FontAwesomeIcon style={{ margin: 'auto' }} icon={faAngleRight} />
                            </div>}
                        />
                    </div>
                    {!isMobileView && <div ref={extraContentRef} style={{ position: "absolute", top: 0, right: 0, paddingTop: "2px" }}>{extraContent}</div>}
                </div>
            }
        >
            {
                defaultTabKey && <TabPane
                    style={{ padding: "0 8px", height: "100%" }}
                    closable={false}
                    tab={
                        <div style={{ display: "flex", width: isMobileView ? 55 : (mainTabTitle ? 85 : 75) }}>
                            <div style={{ width: "20%" }}>
                                <FontAwesomeIcon className="color-blue-5" icon={faLineColumns} />
                            </div>
                            <div className="trim-overflown-text" style={{ width: "80%", padding: "0 4px 0 8px" }}>{mainTabTitle || "Grid"}</div>
                        </div>
                    }
                    key={defaultTabKey}
                />
            }
            {panes?.map((pane: INavTabs.TabPaneProps) => (
                <TabPane
                    style={{ padding: "0 8px" }}
                    closable={false}
                    tab={
                        <div style={{ display: "flex", width: isMobileView ? "100px" : "150px" }}>
                            <div style={{ width: "10%" }}>{pane?.icon}</div>
                            <div className="trim-overflown-text" style={{ width: "95%", padding: "0 4px 0 8px" }}>
                                <Tooltip placement="bottom" title={pane.title}>
                                    {pane.title}
                                </Tooltip>
                                </div>
                            <div>
                                <Badge dot={pane?.updated}>
                                    <span onClick={(e) => { e.stopPropagation(); }}><CloseConfirm isNotifications={isNotfModule} onTabClose={() => removePane([pane])} isTabUpdated={pane.updated} /></span>
                                </Badge>
                            </div>
                        </div>
                    }
                    key={pane.key}
                >
                    {pane.content}
                </TabPane>
            ))}
        </Tabs>
    </>
}

export default NavTabs;

const CloseConfirm = ({ onTabClose, isTabUpdated, isNotifications }: any) => {
    return !isTabUpdated ? <FontAwesomeIcon onClick={onTabClose} className="color-gray-5 font-size-sm tab-cls-icn" icon={faTimes} /> :
        <Popconfirm
            placement="bottom"
            icon={null}
            overlayClassName="tab-closure-cnfrmation"
            title={
                <div style={{ maxWidth: "320px" }}>
                    <Title style={{ fontSize: "28px", padding: 0, margin: 0 }} className="color-blue-5">{isNotifications ? "This Notification is not saved. Are you sure you want to leave?" : "Abandon unsubmitted Service Request?"}</Title>
                    <br />
                    {!isNotifications && <div className="color-gray-5 padding-bottom-md">Work will not be saved.</div>}
                </div>
            }
            onCancel={onTabClose}
            okText={isNotifications ? "Return" : "No, continue editing"}
            cancelText={isNotifications ? "Leave" : "Abandon"}
            okButtonProps={{
                style: { height: "36px" },
                className: "padding-xs"
            }}
            cancelButtonProps={{
                style: { height: "36px" },
                className: "color-pink border-pink padding-xs margin-right-xs"
            }}
        >
            <FontAwesomeIcon className="color-gray-5 font-size-sm tab-cls-icn" icon={faTimes} />
        </Popconfirm>
}
